/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router';
import _ from 'lodash';
import {
    hooks as commonHooks,
    types as commonTypes,
    analytics,
    utils as commonUtils,
} from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import subSectionsStyles, { themeSubSectionStyles, tabsPanelStyles } from './SubSections.styles';

const CHILDREN_PLACEHOLDER_ID = 'children';

interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}

interface Section {
    id: string;
    sectionId?: string;
    subSectionLabel?: string;
    toggleLabel?: string;
}
interface SubSection {
    sectionId: string;
    label: string;
    addToggleLabels?: boolean;
    toggleSectionsList?: string[];
    defaultToggleSection: string;
    analyticsLabel?: string;
}
interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    subSectionsList: SubSection[];
    defaultActiveSubSection: number;
    functionToTransform?: string;
    isTransformData?: boolean;
    typographyClass?: string;
    isRenderAsTabs?: boolean;
    preSelectTabWithUrl?: boolean;
    useSectionId?: boolean;
    disableUrl?: boolean;
    arrowIconType?: '' | 'FontAwesome icon' | 'Image';
    arrowIconLeft?: string;
    arrowIconRight?: string;
    arrowImageLeft?: string;
    arrowImageRight?: string;
    useAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
    lazyLoadSections?: boolean;
    useLowerCase?: boolean;
}

const SubSections = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const appContext = commonHooks.useAppContext();
    const { Placeholder } = appContext;
    const { data } = props;
    const {
        styles,
        subSectionsList,
        defaultActiveSubSection,
        isTransformData,
        functionToTransform,
        isRenderAsTabs,
        preSelectTabWithUrl,
        arrowIconType,
        arrowIconLeft,
        arrowIconRight,
        arrowImageLeft,
        arrowImageRight,
        useAnalytics,
        dynamicEvents,
        lazyLoadSections,
        useLowerCase,
        ...rest
    } = widgetProperties || props;

    const i18n = commonHooks.useLocalization();
    const tracking = analytics.useTracking();

    const [activeSection, setActiveSection] = useState<Section>();
    const [activeSubSection, setActiveSubSection] = useState<string>('');
    const [activeToggleSection, setActiveToggleSection] = useState<string>('');
    const [toggleSectionsList, setToggleSectionsList] = useState<string[]>();

    // State to store the width of the section labels container
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [containerScrollLeft, setContainerScrollLeft] = useState<number>(0);

    // Ref for the container
    const containerRef = useRef<HTMLDivElement | null>(null);

    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const theme = data?.themeSelected || (props as commonTypes.WidgetInstanceProps).theme;
    // generate sections
    let sections: Section[] | any = [];

    let hideSubSectionLabels: number[] = [];
    const location = useLocation();

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedData =
            isTransformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, functionToTransform, {
                data,
                JsonQuery,
            });

        if (transformedData) {
            hideSubSectionLabels = _.isArray(transformedData.subSectionLabelsToHide)
                ? transformedData.subSectionLabelsToHide
                : [];
        }
    } catch (e) {
        console.log(e);
    }

    if (subSectionsList && !_.isEmpty(subSectionsList)) {
        subSectionsList.forEach((s: SubSection, index: number) => {
            const mappedSections = s?.toggleSectionsList?.length
                ? s.toggleSectionsList.map((t: string, i: number) => ({
                      id: `section-${index}${i}`,
                      sectionId: s?.sectionId,
                      subSectionLabel: s?.label,
                      toggleLabel: t,
                  }))
                : [
                      {
                          id: `subSection-${index}`,
                          sectionId: s?.sectionId,
                          subSectionLabel: s?.label,
                      },
                  ];
            sections = [...sections, ...mappedSections];
        });
    }
    // set initial states
    useEffect(() => {
        if (sections?.length > 0) {
            const hash = location.hash;

            const activeSubSection: number =
                defaultActiveSubSection && defaultActiveSubSection <= subSectionsList?.length
                    ? defaultActiveSubSection - 1
                    : 0;
            const _subSectionsList = subSectionsList.filter(
                (_, id: number) => !hideSubSectionLabels.includes(id + 1)
            );
            let activeTabIndex = -1;
            if (preSelectTabWithUrl) {
                activeTabIndex = _subSectionsList.findIndex((l) => {
                    if (rest?.useSectionId) {
                        // Ensure you return the result of includes
                        return hash?.includes(l.sectionId.replace(/ /g, ''));
                    } else {
                        // Return the result of includes for section label
                        return hash?.includes(l.label.replace(/ /g, ''));
                    }
                });
            }

            if (_subSectionsList.length > 0 && _subSectionsList[activeSubSection]) {
                if (rest?.useSectionId) {
                    setActiveSubSection(
                        _subSectionsList[activeTabIndex >= 0 ? activeTabIndex : activeSubSection]
                            .sectionId
                    );
                } else {
                    setActiveSubSection(
                        _subSectionsList[activeTabIndex >= 0 ? activeTabIndex : activeSubSection]
                            .label
                    );
                }

                const _toggleLabels = _subSectionsList[activeSubSection]?.toggleSectionsList;
                if (_toggleLabels?.length) {
                    setToggleSectionsList(_toggleLabels);
                    setActiveToggleSection(_subSectionsList[activeSubSection].defaultToggleSection);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections?.length]);

    // update active section
    useEffect(() => {
        if (activeSubSection && activeToggleSection) {
            const section = sections.find((s: Section) => {
                if (rest?.useSectionId) {
                    return (
                        s.sectionId === activeSubSection && s.toggleLabel === activeToggleSection
                    );
                } else {
                    return (
                        s.subSectionLabel === activeSubSection &&
                        s.toggleLabel === activeToggleSection
                    );
                }
            });
            setActiveSection(section);
        } else if (_.isEmpty(toggleSectionsList)) {
            let section;
            if (rest?.useSectionId) {
                section = sections.find((s: Section) => s.sectionId === activeSubSection);
            } else {
                section = sections.find((s: Section) => s.subSectionLabel === activeSubSection);
            }
            setActiveSection(section);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSubSection, activeToggleSection]);

    useEffect(() => {
        if (activeSubSection && preSelectTabWithUrl && !rest?.disableUrl) {
            const newHash = activeSubSection.replace(/ /g, '');
            // Directly set the window location hash without modifying the pathname or search
            window.location.hash = newHash;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSubSection, preSelectTabWithUrl]);

    // Calculate and update the container width based on the window width
    const updateContainerWidth = () => {
        if (containerRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const maxWidth = containerRect.width;

            setContainerWidth(maxWidth);
        }
    };

    useEffect(() => {
        // Initialize container width on mount and whenever sections change
        updateContainerWidth();
        // Attach window resize listener to update container width
        window.addEventListener('resize', updateContainerWidth);

        return () => {
            window.removeEventListener('resize', updateContainerWidth);
        };
    }, []);

    // Handle arrow navigation and scrolling
    const handleArrowNavigation = (scrollAmount: number) => {
        if (containerRef.current) {
            const newScrollLeft = Math.max(containerRef.current.scrollLeft + scrollAmount, 0);
            containerRef.current.scrollLeft = newScrollLeft;
            setContainerScrollLeft(newScrollLeft);
        }
    };

    const handleTabClick = (section: any) => {
        if (rest?.useSectionId) {
            setActiveSubSection(section?.sectionId);
            const _activeSubSection = subSectionsList.filter(
                (list) => list?.sectionId === section?.sectionId
            )[0];
            setToggleSectionsList(_activeSubSection?.toggleSectionsList);

            if (!_activeSubSection?.toggleSectionsList?.length) {
                setActiveToggleSection('');
            } else {
                setActiveToggleSection(_activeSubSection?.defaultToggleSection);
            }
        } else {
            setActiveSubSection(section?.label);
            const _activeSubSection = subSectionsList.filter(
                (list) => list?.label === section?.label
            )[0];
            setToggleSectionsList(_activeSubSection?.toggleSectionsList);

            if (!_activeSubSection?.toggleSectionsList?.length) {
                setActiveToggleSection('');
            } else {
                setActiveToggleSection(_activeSubSection?.defaultToggleSection);
            }
        }

        tracking({
            event: 'Selected Tab',
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            selectedTabValue: section?.analyticsLabel,
            useLowerCase: useLowerCase,
        });
    };

    return (
        <div
            className={`sub-section ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            css={css`
                ${!theme ? subSectionsStyles : themeSubSectionStyles}
                ${isRenderAsTabs ? tabsPanelStyles : ''}
                ${subSectionsStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            data-testid="sub-section"
        >
            <div ref={containerRef} className={`section-labels`}>
                <div className="sub-section-labels">
                    {subSectionsList?.map(
                        (s: SubSection, id: number) =>
                            !hideSubSectionLabels.includes(id + 1) && (
                                <div
                                    key={s?.label}
                                    id={i18n.format(s?.sectionId || `sub-section-${id}`, data)}
                                    className={`sub-section-label ${
                                        rest?.useSectionId
                                            ? activeSubSection === s?.sectionId
                                                ? 'active'
                                                : ''
                                            : activeSubSection === s?.label
                                              ? 'active'
                                              : ''
                                    } ${data?.locale === 'fr-CA' ? 'french' : ''}`}
                                    data-testid={`label-test-${id + 1}`}
                                    dangerouslySetInnerHTML={{
                                        __html: i18n.format(s?.label || '', data),
                                    }}
                                    onClick={() => handleTabClick(s)}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleTabClick(s);
                                        }
                                    }}
                                />
                            )
                    )}
                    {appContext.isClient && window.innerWidth <= 767 && (
                        <div className={`arrow-icons-container`}>
                            {arrowIconType === 'FontAwesome icon' ? (
                                <>
                                    {containerScrollLeft > 0 && (
                                        <i
                                            className={`arrow-icon arrow-icon-left 
                                            fa ${arrowIconLeft}`}
                                            onClick={() => handleArrowNavigation(-100)}
                                        />
                                    )}

                                    {containerRef.current &&
                                        containerRef.current?.scrollWidth >
                                            containerWidth + containerScrollLeft && (
                                            <i
                                                className={`arrow-icon arrow-icon-right 
                                                fa ${arrowIconRight}`}
                                                onClick={() => handleArrowNavigation(100)}
                                            />
                                        )}
                                </>
                            ) : (
                                arrowIconType === 'Image' && (
                                    <>
                                        {containerRef.current &&
                                            containerRef.current.scrollLeft > 0 && (
                                                <img
                                                    className={'arrow-icon arrow-image-left'}
                                                    src={arrowImageLeft}
                                                    alt={'Left arrow'}
                                                    onClick={() => handleArrowNavigation(-100)}
                                                />
                                            )}
                                        {containerRef.current &&
                                            containerRef.current?.scrollWidth >
                                                containerWidth + containerScrollLeft && (
                                                <img
                                                    className={'arrow-icon arrow-image-right'}
                                                    src={arrowImageRight}
                                                    alt={'Right arrow'}
                                                    onClick={() => handleArrowNavigation(100)}
                                                />
                                            )}
                                    </>
                                )
                            )}
                        </div>
                    )}
                </div>
                <div className="toggle-labels">
                    {toggleSectionsList?.map((t: string) => (
                        <label
                            key={t}
                            className={`toggle-label ${activeToggleSection === t ? 'active' : ''}`}
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(t || '', data),
                            }}
                            onClick={() => setActiveToggleSection(t)}
                        />
                    ))}
                </div>
            </div>

            {lazyLoadSections ? (
                <>
                    {sections
                        .filter((s: Section) => s.id === activeSection?.id)
                        .map((s: Section) => (
                            <div key={s.id} className={`sub-section-content show`}>
                                <Placeholder
                                    key={s.id}
                                    contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-${s.id}`}
                                    parentWidgetInstanceId={widgetInstance?.id}
                                    data={data}
                                />
                            </div>
                        ))}
                </>
            ) : (
                <>
                    {sections.map((s: Section) => (
                        <div
                            key={s.id}
                            className={`sub-section-content ${
                                activeSection?.id === s.id ? 'show' : 'hide'
                            }`}
                            data-testid={s.id}
                        >
                            <Placeholder
                                key={s.id}
                                contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-${s.id}`}
                                parentWidgetInstanceId={widgetInstance?.id}
                                data={data}
                            />
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default SubSections;
